import React, { Component } from 'react';
import {
  BEFORE_YOUR_SCREEN,
  YOUR_FINANCIAL_CIRCUMSTANCES,
  YOUR_LOAN_OPTIONS,
  INFORM_CHOICE
} from 'constants/paths';
import { isBackButtonHidden } from 'utils/isBackButtonHidden';

class NavigationGuard extends Component {
  constructor(props) {
    super(props);

    this.redirectBack = this.redirectBack.bind(this);
    this.redirectForward = this.redirectForward.bind(this);
    this.setLocationToRender = this.setLocationToRender.bind(this);

    this.state = {
      locationToRender: props.location, // the location we trick <Switch> into rendering.
      previousLocation: '__ORIGIN__', // just any value that isn't null, undefined or an actual pathname
      firstBack: true // always reset to true after a PUSH event (or blocked FORWARD? test that theory)
    };
  }

  isRedirecting = false; // Did the previous update trigger a redirect? If so these new props are the location to redirect to.

  shouldComponentUpdate(nextProps, nextState) {
    // only update the location if the pathname has changed
    return (
      nextProps.location.pathname !== this.state.locationToRender.pathname ||
      this.state.locationToRender.pathname !== nextState.locationToRender.pathname ||
      this.isRedirecting === true
    );
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.locationToRender.pathname !== prevState.locationToRender.pathname) {
      if (
        this.props.action === 'POP' &&
        prevState &&
        prevState.locationToRender &&
        ((!this.props.originPath && prevState.locationToRender.pathname === BEFORE_YOUR_SCREEN) ||
          (this.state.locationToRender.pathname === BEFORE_YOUR_SCREEN &&
            prevState.locationToRender.pathname === YOUR_FINANCIAL_CIRCUMSTANCES))
      ) {
        this.props.onRedirectToOrigin && this.props.onRedirectToOrigin();
      }
    }

    if (this.props.action === 'PUSH' || this.props.action === 'REPLACE') {
      this.setLocationToRender(this.props.location);
    }

    if (this.props.action === 'POP') {
      // we've already called goBack or goForward from this component, allow to pass.
      if (this.isRedirecting === true) {
        this.isRedirecting = false;
      } else {
        const isEditablePath =
          this.props.location.state &&
          this.props.location.state.previous &&
          this.state.locationToRender.state &&
          this.state.locationToRender.state.editablePaths &&
          this.state.locationToRender.state.editablePaths.includes(
            this.props.location.state.previous
          );
        isEditablePath && this.props.toggleBackButton(isEditablePath);
        // Test the Good Scenario: Going backward when allowed
        const isForward =
          !isEditablePath &&
          Boolean(
            this.props.location.state &&
              this.props.location.state.previous &&
              this.props.location.state.previous === this.state.locationToRender.pathname
          );

        const isBack = Boolean(
          this.state.firstBack === true &&
            isForward === false &&
            this.state.previousLocation &&
            (this.state.previousLocation === '__ORIGIN__' ||
              this.state.previousLocation.pathname === this.props.location.pathname)
        );

        const isSecondBack = Boolean(
          isForward === false &&
            this.state.firstBack === false &&
            this.props.location.pathname !== this.state.previousLocation.pathname
        );

        const isForwardAfterFirstBack = Boolean(
          this.state.firstBack === false &&
            this.props.location.pathname === this.state.previousLocation.pathname
        );

        if (isBack) {
          if (
            isBackButtonHidden(this.state.locationToRender.pathname) ||
            this.props.lockNavigation === true
          ) {
            this.redirectForward();
          } else if (
            this.state.locationToRender.pathname === INFORM_CHOICE ||
            (this.props.location.state &&
              this.props.location.state.previous === BEFORE_YOUR_SCREEN &&
              this.state.locationToRender.pathname === YOUR_LOAN_OPTIONS)
          ) {
            this.props.onRedirectToOrigin && this.props.onRedirectToOrigin();
          } else {
            this.setLocationToRender(this.props.location, false);
          }
        } else if (isSecondBack) {
          if (isForwardAfterFirstBack) {
            this.redirectBack(true);
          } else {
            if (
              isBackButtonHidden(this.state.locationToRender.pathname) ||
              this.props.lockNavigation === true
            ) {
              this.redirectForward();
            } else {
              this.setLocationToRender(this.props.location);
            }
          }
        } else if (isForward) {
          this.redirectBack(true);
        }
      }
    }

    // call the onLocationChange callback with the new location object as a parameter.
    this.props.onLocationChange && this.props.onLocationChange(this.state.locationToRender);
  }

  redirectBack() {
    this.props.goBack();
  }

  redirectForward() {
    this.props.goForward();
  }

  /**
   * Updates internal component state when the app has decided to render a different
   * location in front of the User.
   * @param {String} location
   * @param {Function} callback
   */
  setLocationToRender(location, firstBack = true) {
    this.isRedirecting = false;
    this.setState(
      () => {
        return {
          locationToRender: location,
          previous: this.state.locationToRender,
          firstBack
        };
      },
      () => {
        /**
         * If we've been provided with an `onLocationChange` callback function
         * via props, we'll call it with the `location` object.
         */
        this.props.onLocationChange && this.props.onLocationChange(location);
      }
    );
  }

  render() {
    /**
     * Render children passing an additional "render prop" that can be used
     * to provide our intended route path to a react-router component.
     * This allows us to either temporarily or permanently differ the path
     * used from the path that comes from the browser's {@link https://developer.mozilla.org/en-US/docs/Web/API/History history API}.
     *
     * This allows us to give the effect of "blocking" backward navigation to
     * the User where it makes sense, either because it makes sense in their
     * journey, or to protect them from triggering additional unexpected errors
     * in the event that something has gone wrong.
     *
     * @example
     * <NavigationGuard {...prop}>
     * {({ locationToRender }) => {
     *  <Switch location={locationToRender}>
     *    {...routes}
     *  </Switch>
     * }}
     */
    const renderedChildren = this.props.children(this.state.locationToRender);
    return renderedChildren && React.Children.only(renderedChildren);
  }
}

export { NavigationGuard };
